<template>
    <ui-container class="space-y-8 py-8">

        <!-- Header. -->
        <ui-header :title="`${item.make} ${item.model}`" :subtitle="item.vrn" :image="item.image">
            <template #actions>
                <ui-button-new icon="fas fa-pencil" @click="edit()">Edit</ui-button-new>
                <ui-button-new icon="fas fa-trash" danger @click="openDeleteModal">Delete</ui-button-new>
            </template>
        </ui-header>

        <ui-tabs active="overview" :tabs="tabs" #default="{ isActive }">
            <!-- Overview. -->
            <div v-if="isActive('overview')">
                <ui-data-grid>
                    <ui-data-grid-item label="Reference" :value="item.reference"></ui-data-grid-item>
                    <ui-data-grid-item label="Registration" :value="item.vrn"></ui-data-grid-item>
                    <ui-data-grid-item label="Make" :value="item.make"></ui-data-grid-item>
                    <ui-data-grid-item label="Model" :value="item.model"></ui-data-grid-item>
                    <ui-data-grid-item label="Created By" :value="item.created_by.name"></ui-data-grid-item>
                    <ui-data-grid-item label="Created At" :value="item.created_at"></ui-data-grid-item>
                    <ui-data-grid-item label="Ownership Type">{{ item.ownership | ucfirst }}</ui-data-grid-item>
                    <ui-data-grid-item label="Sold Date" :value="item.sold_at" v-if="item.ownership === 'sold'"></ui-data-grid-item>
                    <ui-data-grid-item label="Extra Attributes" class="lg:row-span-4 lg:row-start-1 lg:col-start-2">
                        <ui-boolean-list :booleans="extraAttributes"></ui-boolean-list>
                    </ui-data-grid-item>
                </ui-data-grid>
            </div>

            <!-- Dates. -->
            <div v-else-if="isActive('dates')">
                <ui-data-grid>
                    <ui-data-grid-item label="MOT Due" :value="item.mot_due"></ui-data-grid-item>
                    <ui-data-grid-item label="Service Due" :value="item.service_due"></ui-data-grid-item>
                    <ui-data-grid-item label="Road Tax Due" :value="item.road_tax_due"></ui-data-grid-item>
                    <ui-data-grid-item label="Purchase Date" :value="item.purchase_date"></ui-data-grid-item>
                    <ui-data-grid-item label="Sold At" :value="item.sold_at" v-if="item.sold"></ui-data-grid-item>
                </ui-data-grid>
            </div>

            <!-- Lease Details. -->
            <div v-else-if="isActive('lease_details')">
                <ui-data-grid>
                    <ui-data-grid-item label="Lease Commence" :value="item.lease_commence"></ui-data-grid-item>
                    <ui-data-grid-item label="Lease Company" :value="item.lease_company_name"></ui-data-grid-item>
                    <ui-data-grid-item label="Lease Advance Payment">
                        &pound;{{ item.lease_advance_payment || '0.00' }}
                    </ui-data-grid-item>
                    <ui-data-grid-item :label="`Followed By ${item.lease_followed_by_number} ${item.lease_followed_by_frequency_uc} payments of`">
                        &pound;{{ item.lease_followed_by_value || '0.00' }}
                    </ui-data-grid-item>
                    <ui-data-grid-item label="Lease Final Payment">
                        &pound;{{ item.lease_final_payment || '0.00' }}
                    </ui-data-grid-item>
                    <ui-data-grid-item label="Total Cost Of Lease">
                        &pound;{{ item.totalLeasePayment() }}
                    </ui-data-grid-item>
                </ui-data-grid>
            </div>

            <!-- Ownership. -->
            <div v-else-if="isActive('ownership')">
                <ui-data-grid>
                    <ui-data-grid-item label="Ownership Type">{{ item.ownership | ucfirst }}</ui-data-grid-item>
                    <ui-data-grid-item label="Purchase Date" :value="item.purchase_date"></ui-data-grid-item>
                    <ui-data-grid-item label="Purchase Price" :value="item.purchase_price"></ui-data-grid-item>
                </ui-data-grid>
            </div>

            <!-- Maintenance. -->
            <div v-else-if="isActive('maintenance')">
                <ui-mini-crud
                    :fetch="() => item.logs.data"
                    add-button-text="Add Log Entry"
                    empty-state-text="This vehicle has no log entry."
                >
                    <template #show="{ item, select }">
                        <ui-mini-crud-card
                            :title="`${item.type} at ${item.mileage} miles`"
                            :subtitle="[item.date, item.carried_out_by]"
                            @edit="select"
                        >
                            <p v-if="item.notes" class="mt-2 text-gray-500 text-sm">
                                <span class="font-semibold">Notes: </span>
                                <span v-text="item.notes" class="whitespace-pre-wrap"></span>
                            </p>
                            <div v-if="item.files.length" class="mt-2 text-gray-500 text-sm">
                                <span class="font-semibold">Files: </span>
                                <ul>
                                    <li v-for="file in item.files"><a target="_blank" class="underline" :href="file.download">{{ file.name }}</a></li>
                                </ul>
                            </div>
                        </ui-mini-crud-card>
                    </template>
                    <template #edit="{ item: log, unselect }">
                        <assets-vehicles-logs-form :vehicle="item" :item="log" :close="unselect"></assets-vehicles-logs-form>
                    </template>
                    <template #create="{ addItem, setCreateMode }">
                        <assets-vehicles-logs-form :vehicle="item" :add-item="addItem" :close="() => setCreateMode(false)"></assets-vehicles-logs-form>
                    </template>
                </ui-mini-crud>
            </div>

            <!-- Milage. -->
            <div v-else-if="isActive('milage')">
                <ui-data-grid>
                    <ui-data-grid-item label="Requires Mileage">{{ item.milage_required | human_bool }}</ui-data-grid-item>
                    <ui-data-grid-item label="Mileage Requirement Frequency" :value="item.milage_frequency"></ui-data-grid-item>
                    <ui-data-grid-item wide label="Log New Mileage">
                        <assets-vehicles-new-mileage-form :vehicle="item"></assets-vehicles-new-mileage-form>
                    </ui-data-grid-item>
                    <ui-data-grid-item wide label="Mileage Logs" :key="`${item.id}-mileagelogs-${item.mileagelogs.data.length}`">
                        <ui-mini-crud :fetch="() => item.mileagelogs.data" empty-state-text="This vehicle has no mileage logs. Use the form above to log a new mileage.">
                            <template #show="{ item }">
                                <ui-mini-crud-card
                                    :title="item.mileage"
                                    :subtitle="[item.date, `Added by ${item.created_by.name}`]"
                                ></ui-mini-crud-card>
                            </template>
                        </ui-mini-crud>
                    </ui-data-grid-item>
                </ui-data-grid>
            </div>

            <!-- Drivers. -->
            <div v-else-if="isActive('drivers')">
                <ui-data-grid>
                    <ui-data-grid-item wide label="Current Driver">
                        <assets-vehicles-update-driver-form :vehicle="item"></assets-vehicles-update-driver-form>
                    </ui-data-grid-item>
                    <ui-data-grid-item wide label="Previous Drivers" :key="`${item.id}-previousdrivers-${item.previousdrivers.data.length}`">
                        <ui-mini-crud :fetch="() => item.previousdrivers.data" empty-state-text="There are no previously logged drivers to this vehicle.">
                            <template #show="{ item }">
                                <ui-mini-crud-card
                                    :title="item.name"
                                    :subtitle="[`Resigned at ${item.resigned_at}`, `Added by ${item.created_by.name}`]"
                                ></ui-mini-crud-card>
                            </template>
                        </ui-mini-crud>
                    </ui-data-grid-item>
                </ui-data-grid>
            </div>

            <!-- Files. -->
            <div v-else-if="isActive('files')">
                <filesystem
                    compact="true"
                    :filesystem-id="item.filesystem_id"
                ></filesystem>
            </div>
        </ui-tabs>

    </ui-container>
</template>

<script>
export default {
    props: {
        item: Object,
        edit: Function,
        removeItem: Function,
    },
    computed: {
        tabs () {
            return [
                { value: 'overview', label: 'Overview' },
                { value: 'dates', label: 'Dates' },
                { value: 'lease_details', label: 'Lease Details', when: this.item.ownership === 'leased' },
                { value: 'ownership', label: 'Ownership', when: this.item.ownership === 'owned' },
                { value: 'maintenance', label: 'Maintenance' },
                { value: 'milage', label: 'Milage' },
                { value: 'drivers', label: 'Drivers' },
                { value: 'files', label: 'Files' },
            ]
        },
        extraAttributes () {
            return [
                { value: this.item.fleet_insurance, label: 'Has Fleet Insurance' },
                { value: this.item.cc_account, label: 'CC Account' },
                { value: this.item.cc_autopay, label: 'CC Autopay' },
                { value: this.item.parking_account, label: 'Parking Account' },
                { value: this.item.dart_charge, label: 'Dart Charge' },
            ]
        },
    },
    methods: {
        openDeleteModal () {
            const title = 'Are you sure you want to delete "' + this.item.vrn + '"?'
            const description = 'Deleting this vehicle is irreversible. Are you sure you want to delete it?'
            eventBus.$emit('delete-modal-open', title, description, async () => {
                await this.item.delete()
                this.removeItem(this.item)

            });
        },
    },
}
</script>
